import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useStore } from '../store';
import { getAllTableWidgets } from '../utils/pageConfigHelpers';

import { createTableFilterSearchParamKey } from './usePresetTableFiltersFromUrl';
import {
  useCurrentStoreFilters,
  UseSyncFiltersToSearchParams,
  useSyncFiltersToSearchParams,
} from './useSyncFiltersToSearchParams';

type UseSyncTableFiltersToSearchParams = Pick<
  UseSyncFiltersToSearchParams,
  'navigationData' | 'navItemId' | 'pageData'
>;

export function useSyncTableFiltersToSearchParams({
  navigationData,
  navItemId,
  pageData,
}: UseSyncTableFiltersToSearchParams) {
  const tableWidgets = useMemo(
    () => getAllTableWidgets(pageData?.contents ?? []),
    [pageData?.contents]
  );

  const filterDescriptors = useMemo(() => {
    return tableWidgets.map((widget) => ({
      instanceId: widget.id,
      config: widget.entity.config.filters,
    }));
  }, [tableWidgets]);

  const filtersToSet = useCurrentStoreFilters(
    filterDescriptors,
    createTableFilterSearchParamKey
  );

  const filterData = useStore(useShallow((state) => state.filterData));

  const allFilterKeys = useMemo(
    () =>
      filterDescriptors.flatMap((fd) => {
        if (
          !filterData[fd.instanceId]?.meta.loaded ||
          !filterData[fd.instanceId]?.meta.initialised
        ) {
          return [];
        }
        return (
          fd.config?.map((fc) =>
            createTableFilterSearchParamKey(fd.instanceId, fc.key)
          ) ?? []
        );
      }),
    [filterDescriptors, filterData]
  );

  useSyncFiltersToSearchParams({
    filtersToSet,
    allFilterUrlKeys: allFilterKeys,
    navigationData,
    navItemId,
    pageData,
  });
}
